import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleCategories, allArticles } from '@/mocks/articles';

const colorMap: Record<string, { badge: string; tab: string; line: string }> = {
  primary: {
    badge: 'bg-primary-100 text-primary-700',
    tab: 'bg-primary-500 text-background-50',
    line: 'border-primary-500',
  },
  accent: {
    badge: 'bg-accent-100 text-accent-700',
    tab: 'bg-accent-500 text-background-50',
    line: 'border-accent-500',
  },
  secondary: {
    badge: 'bg-secondary-100 text-secondary-700',
    tab: 'bg-secondary-500 text-background-50',
    line: 'border-secondary-500',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'news');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSlug]);

  const currentCategory = useMemo(
    () => articleCategories.find((c) => c.slug === activeSlug) || articleCategories[0],
    [activeSlug],
  );

  const filteredArticles = useMemo(
    () => allArticles.filter((a) => a.category === activeSlug),
    [activeSlug],
  );

  const colors = colorMap[currentCategory.color] || colorMap.primary;

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.label} - 2026世界杯`,
    description: `2026美加墨世界杯${currentCategory.label}专区，为您提供最新、最全面的${currentCategory.label}相关信息。`,
    url: 'http://cefotiam.com/category/all',
    isPartOf: {
      '@type': 'WebSite',
      name: '2026世界杯官网',
      url: 'http://cefotiam.com/',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'http://cefotiam.com/' },
        { '@type': 'ListItem', position: 2, name: currentCategory.label, item: 'http://cefotiam.com/category/all' },
      ],
    },
  };

  return (
    <>
      <Navbar />
      <main>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(categoryJsonLd) }}
        />

        <section className="pt-24 md:pt-28 pb-12 md:pb-16 bg-background-100">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <nav aria-label="breadcrumb" className="mb-6">
              <ol className="flex items-center gap-1.5 text-sm">
                <li>
                  <Link to="/" className="text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">
                    首页
                  </Link>
                </li>
                <li>
                  <span className="text-foreground-400 mx-1">/</span>
                </li>
                <li>
                  <span className="text-foreground-800 font-medium">{currentCategory.label}</span>
                </li>
              </ol>
            </nav>

            <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950 mb-8">
              {currentCategory.label}
            </h1>

            <div className="flex flex-wrap gap-2 mb-10">
              {articleCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to="/category/all"
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? colors.tab
                      : 'bg-background-50 text-foreground-600 border border-background-200 hover:border-foreground-300'
                  }`}
                >
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden transition-all duration-300 hover:border-primary-300/50 cursor-pointer"
                  >
                    <div className="relative overflow-hidden h-48">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${colors.badge}`}>
                          {article.categoryLabel}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h2 className="font-heading font-semibold text-base text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
                        {article.summary}
                      </p>
                      <div className="flex items-center gap-1.5 text-xs text-foreground-500">
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-time-line text-xs"></i>
                        </span>
                        {article.date}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </span>
                <p className="text-foreground-500 text-sm">该分类暂无内容，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}